import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Crown, Scroll } from "lucide-react";

export function RulesModalTrigger({ children, className, testid = "open-rules-modal" }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" data-testid={testid} className={className}>
          {children}
        </button>
      </DialogTrigger>
      <RulesContent />
    </Dialog>
  );
}

/** Controlled rules dialog — opened programmatically (e.g. when a parent tries to submit without signing). */
export function RulesDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <RulesContent />
    </Dialog>
  );
}

function RulesContent() {
  return (
    <DialogContent
      data-testid="rules-modal"
      className="max-w-2xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-[#fff4fa] via-white to-[#fde4ee] border-2 border-[#d4af37]"
    >
      <DialogHeader>
        <div className="flex items-center gap-3">
          <img src="/logo-sm.png" alt="Queenz Like Me" className="h-12 w-12 rounded-full object-cover ring-2 ring-[#d4af37]" />
          <div>
            <p className="font-script text-2xl text-[#d4af37] leading-none">Queenz Like Me</p>
            <DialogTitle className="font-display text-2xl font-bold text-[#4a1466]">
              Rules &amp; Regulations
            </DialogTitle>
          </div>
        </div>
        <DialogDescription className="text-[#5c4a54] mt-2">
          Please read carefully — these guidelines keep every Queen safe, supported, and celebrated.
        </DialogDescription>
      </DialogHeader>

      <div className="mt-4 space-y-5 text-[#1a0f14] text-sm leading-relaxed">
        <Section title="1. Respect & Sisterhood">
          Every Queen is expected to treat mentors, volunteers, and fellow queenz with kindness and
          respect. No bullying, gossip, name-calling, or exclusion. We rise by lifting others.
        </Section>
        <Section title="2. Attendance & Punctuality">
          Consistent attendance is a sign of commitment. Parents must notify us of absences in
          advance whenever possible. Three unexcused absences may forfeit your seat to a queen on
          the waiting list.
        </Section>
        <Section title="3. Dress Code">
          Comfortable, modest, age-appropriate attire. Avoid clothing with offensive language or
          imagery. Special-event dress codes will be communicated in advance.
        </Section>
        <Section title="4. Phones & Devices">
          Phones stay on silent and away during sessions. Photo/video sharing of other queenz is not
          permitted without parental consent and Queenz Like Me approval.
        </Section>
        <Section title="5. Confidentiality">
          What&apos;s shared in the circle stays in the circle. Mentors are mandated reporters and are
          legally required to act on disclosures of harm — otherwise, every queen&apos;s story is honored
          and protected.
        </Section>
        <Section title="6. Health & Safety">
          Parents must disclose allergies, medical conditions, and emergency contacts on the
          registration form. Queenz Like Me reserves the right to dismiss participants whose
          behavior endangers themselves or others.
        </Section>
        <Section title="7. Parent / Guardian Participation">
          Parents are partners in this journey. We expect open communication, timely pickup, and
          support of program values at home.
        </Section>
        <Section title="8. Media Release">
          Photos and short videos may be used for promotional and ministry purposes. Parents may
          opt out in writing at registration; we will honor it without question.
        </Section>
        <Section title="9. Financial Commitment">
          Most programming is free thanks to sponsors. When dues, retreat fees, or supply fees apply,
          payment plans are available — talk to us, never let cost be a barrier.
        </Section>
        <Section title="10. Code of Conduct">
          Queenz commit to honesty, integrity, and the Queenz Like Me values of <em>Character, Courage,
          and Compassion</em>. Violations are addressed privately and restoratively with parents.
        </Section>

        <div className="rounded-2xl border-2 border-[#d4af37] bg-[#fff8e1] p-4 mt-2">
          <p className="font-display text-[#4a1466] font-semibold flex items-center gap-2">
            <Crown className="h-5 w-5 text-[#d4af37]" />
            By participating, you and your queen agree to honor these rules.
          </p>
          <p className="mt-2 text-[#5c4a54]">
            For the full participation agreement and signature line, click below.
          </p>
          <a
            href="/agreement"
            data-testid="rules-modal-agreement-link"
            className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#7d289b] text-white font-semibold text-sm hover:bg-[#4a1466] transition-colors"
          >
            <Scroll className="h-4 w-4" />
            Read &amp; sign the full Participation Agreement
          </a>
        </div>
      </div>
    </DialogContent>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-display font-bold text-[#7d289b] mb-1">{title}</h4>
      <p>{children}</p>
    </div>
  );
}

/** Inline pill-style link to be embedded inside Waitlist, Register, Sponsor sections */
export function RulesPillLink({ testid }) {
  return (
    <RulesModalTrigger
      testid={testid}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#d4af37] bg-[#fff8e1] text-[#7d289b] font-semibold text-sm hover:bg-[#d4af37] hover:text-white transition-all"
    >
      <Scroll className="h-4 w-4" />
      Read the Rules &amp; Regulations
    </RulesModalTrigger>
  );
}
